import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { RiFullscreenExitLine, RiFullscreenLine } from "@remixicon/react";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { useQueryState } from "../../../hooks/useQueryState";
import { Country } from "../../../types/Common";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";
import ReviewPlacesOmniBizCategories from "./ReviewPlacesOmniBizCategories";
import ReviewPlacesOmniCountryCount from "./ReviewPlacesOmniCountryCount";
import ReviewPlacesOmniFillRates from "./ReviewPlacesOmniFillRates";
import ReviewPlacesOmniHealth from "./ReviewPlacesOmniHealth";
import ReviewPlacesOmniLocationNames from "./ReviewPlacesOmniLocationNames";
import ReviewPlacesOmniNames from "./ReviewPlacesOmniNames";
import ReviewPlacesOmniWebsites from "./ReviewPlacesOmniWebsites";

interface PlacesReviewOmniscrapersProps {
  brand_id: string;
  brand_name: string;
  chain_id: string;
}

const PlacesReviewOmniscrapers: React.FC<PlacesReviewOmniscrapersProps> = ({
  brand_id,
  brand_name,
  chain_id,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedOmni, setSelectedOmni] = useQueryState("omni", null);
  const [selectedCountryCode, setSelectedCountryCode] = useQueryState<
    string | null
  >("country", null);

  const { showError } = useError();
  const { apiRequest } = useApi();

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  // Fetch omnis
  const { data: brandOmnis, isLoading: loading } = useQuery({
    queryKey: ["brandOmnis", brand_id],
    queryFn: async () => {
      try {
        const response = await apiRequest<any>(
          `/brand_utils/brand_omni_keys/${brand_id}`,
          {
            method: "GET",
          }
        );
        return response.data || [];
      } catch (error) {
        if (error instanceof Error) {
          showError(`Failed to fetch omnis: ${error.message}`);
        } else {
          showError("Failed to fetch omnis: Unknown error");
        }
        throw error;
      }
    },
    enabled: !!brand_id, // Only fetch when brand_id is set
  });

  // **Fetch Countries using Dependent Query**
  const { data: brandCountries = [], isLoading: loadingCountries } = useQuery<
    Country[]
  >({
    queryKey: ["brandCountries", selectedOmni],
    queryFn: async () => {
      try {
        const response = await apiRequest<Country[]>(
          `/brand_utils/omni_country_list/${selectedOmni}`,
          { method: "GET" }
        );
        return response.data || [];
      } catch (error) {
        if (error instanceof Error) {
          showError(`Failed to fetch countries: ${error.message}`);
        } else {
          showError("Failed to fetch countries: Unknown error");
        }
        return [];
      }
    },
    enabled: !!selectedOmni, // Only fetch when an omni is selected
  });

  // User Instructions for Retractions
  const user_instructions_retractions = {
    section_id: "places-retract-chainid",
    addAnchorLink: false,
    instruction_title: "Review Places To Retract",
    goal: "Ensure values for each of the attributes below, associated with the selected country of operation, are valid, using the metadata as a reference.",
    task_description:
      "The table provided for each attribute compares the metadata with the actual values linked to places in the dataplor database for the brand.",
    task_steps: [
      {
        title:
          "Identify values from the places data that do not correspond to valid values in the metadata",
        instructions: [
          "Review the provided table for each attribute to identify any values associated with places in the database that are missing from the metadata. When metadata is missing, the place needs to be reviewed to assess whether it is valid.",
        ],
      },
      {
        title: "Review step",
        instructions: ["TBD"],
      },
      {
        title: "Retract Chain ID",
        instructions: [
          {
            text: "For values that indicate places that should not be associated with the brand, retract `chain_id` in `Kuebiko`:",
            sublist: [
              "Navigate to the brand in `Kuebiko`.",
              "Select `Review All`.",
              "Open the SQL editor.",
              "Copy and paste the provided SQL code for each attribute to view the places that require `chain_id` to be retracted. Be sure to update with the correct `external_id` and country code.",
            ],
          },
        ],
      },
    ],
  };

  // User Instructions for Location Name & Fill Rates
  const user_instructions_fillrates = {
    section_id: "places-fillrates",
    addAnchorLink: false,
    instruction_title: "Review Location Names and Fill Rates",
    task_steps: [
      {
        title: "Review Location Names",
        instructions: [
          "If you notice any problematic location names, copy the link below and escalate to Karen or Monica.",
        ],
      },
      {
        title: "Review Fill Rates",
        instructions: [
          {
            text: "If the fill rates for any of the following attributes are < 1, copy the link below and escalate to Karen or Monica.",
            sublist: ["Name", "Address", "Business category", "Coordinates"],
          },
        ],
      },
    ],
  };

  return (
    <Box
      sx={
        isFullscreen
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              overflow: "auto",
              p: 5,
              pt: 0,
            }
          : {
              position: "relative",
              width: "auto",
              height: "auto",
              minHeight: "500px",
              overflow: "hidden",
              border: "1px solid #ccc",
              borderRadius: "4px",
              p: 5,
              mt: 2,
              backgroundColor: "#fff",
            }
      }
    >
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          ...(isFullscreen && {
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            width: "100%",
            zIndex: 1000,
            p: 2,
          }),
        }}
      >
        <Typography variant="h5">
          {isFullscreen
            ? `Brand Omniscraper Review: ${brand_name} - ${
                selectedOmni || "Select an omniscraper to review"
              }${
                selectedCountryCode
                  ? `: ${String(selectedCountryCode).toUpperCase()}`
                  : ""
              }`
            : ""}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Open Kuebiko Button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={
              <img
                src="/images/dataplor_logo.svg"
                alt="dataplor logo"
                style={{ width: 20, height: 20 }}
              />
            }
            onClick={() =>
              window.open(
                `https://kuebiko.dataplor.com/brands/${brand_id}/review`,
                "_blank"
              )
            }
          >
            Open Kuebiko
          </Button>
          {/* Fullscreen Toggle Button */}
          <IconButton onClick={toggleFullscreen} sx={{ zIndex: 500 }}>
            {isFullscreen ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: 2, pl: 2, pr: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Places Review by Omniscraper
          </Typography>
          <Typography variant="body1" sx={{ fontStyle: "italic" }}>
            Iterate through one omniscraper at a time. Select an omni key below
            to start the review.
          </Typography>
        </Box>

        {/* Omnis Dropdown */}
        <Box sx={{ mt: 2, width: 300 }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Autocomplete
              options={brandOmnis}
              value={
                selectedOmni
                  ? brandOmnis.find(
                      (omni: { omni_key: string }) =>
                        omni.omni_key === selectedOmni
                    ) || null
                  : null
              }
              onChange={(event, newValue) => {
                setSelectedOmni(newValue?.omni_key || null);
              }}
              isOptionEqualToValue={(option, value) =>
                value && option.omni_key === value.omni_key
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Omni Key"
                  variant="outlined"
                />
              )}
              getOptionLabel={(option) => option.omni_key || "Unknown Omni"}
            />
          )}
        </Box>

        {/* Main content */}
        {selectedOmni && (
          <Box sx={{ mt: 5 }}>
            {/* Omni Health */}
            <ReviewPlacesOmniHealth omni_key={selectedOmni} />

            <Box sx={{ mt: 5 }}>
              <Typography variant="h6" gutterBottom>
                Review Omniscraper Data by Country
              </Typography>
              <Typography sx={{ fontStyle: "italic" }}>
                Iterate through one country at a time. Select a country below to
                update the data for review.
              </Typography>
            </Box>
            {/* Country Code Dropdown */}
            <Box sx={{ mt: 2, mb: 5, width: 300 }}>
              {loadingCountries ? (
                <CircularProgress />
              ) : (
                <Autocomplete
                  options={brandCountries as Country[]}
                  getOptionLabel={(option) =>
                    `${(
                      option.country_code ?? ""
                    ).toUpperCase()}: ${option.country_name?.toLowerCase()}`
                  }
                  filterOptions={(options, { inputValue }) =>
                    options.filter(
                      (option) =>
                        option.country_code
                          ?.toLowerCase()
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        (option.country_name ?? "")
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Country"
                      variant="outlined"
                    />
                  )}
                  value={
                    brandCountries.length > 0
                      ? brandCountries.find(
                          (country) =>
                            country.country_code === selectedCountryCode
                        ) || null
                      : null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelectedCountryCode(newValue.country_code || null);
                    } else {
                      setSelectedCountryCode(null);
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.country_code === value.country_code
                  }
                />
              )}
            </Box>
            {/* Country Specific Content */}
            {selectedCountryCode && (
              <Box>
                <BrandReviewInstructions {...user_instructions_retractions} />
                <ReviewPlacesOmniBizCategories
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                />
                <ReviewPlacesOmniNames
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                />
                <ReviewPlacesOmniWebsites
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                />
                <ReviewPlacesOmniCountryCount
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                />

                <BrandReviewInstructions {...user_instructions_fillrates} />
                <ReviewPlacesOmniLocationNames
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                  chain_id={chain_id}
                />
                <ReviewPlacesOmniFillRates
                  omni_key={selectedOmni}
                  country_code={selectedCountryCode}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PlacesReviewOmniscrapers;
